import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

const DENSITY = {
  comfortable: 'text-[13px] [&_th]:px-5 [&_th]:py-2.5 [&_th]:text-xs [&_td]:px-5 [&_td]:py-3',
  compact: 'text-xs [&_th]:px-2.5 [&_th]:py-1.5 [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:font-mono',
} as const;

interface TableProps {
  children: ReactNode;
  density?: keyof typeof DENSITY;
  /** Scroll vertically inside this height (header stays visible). */
  maxHeight?: number;
}

/**
 * Hairline-ruled data table; scrolls horizontally on narrow screens.
 * Cells opt in to right-aligned numbers with `className="num"`; rows become clickable with `is-link`.
 */
export function Table({ children, density = 'comfortable', maxHeight }: TableProps) {
  return (
    // `relative` keeps absolutely positioned sr-only labels inside the scroller, so they cannot widen the page.
    <div className="relative overflow-auto" style={maxHeight ? { maxHeight } : undefined}>
      <table
        className={cn(
          'w-full border-collapse',
          '[&_th]:sticky [&_th]:top-0 [&_th]:z-[1] [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-line [&_th]:bg-surface-sunken [&_th]:text-left [&_th]:font-medium [&_th]:text-fg-muted',
          '[&_td]:whitespace-nowrap [&_td]:border-b [&_td]:border-line [&_td]:align-middle',
          '[&_tbody_tr:last-child_td]:border-b-0',
          '[&_.num]:text-right [&_.num]:font-mono [&_.num]:tabular-nums',
          '[&_tr.is-link]:cursor-pointer [&_tr.is-link:hover_td]:bg-hover',
          DENSITY[density],
        )}
      >
        {children}
      </table>
    </div>
  );
}
